import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { UtensilsIcon, ShieldCheckIcon, ActivityIcon, ClipboardListIcon, CheckCircleIcon } from 'lucide-react';
export function GTubePage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useScrollAnimation();
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-797">
      <PageBanner title="Enteral Nutrition (G-Tube Care)" subtitle="Expert Gastrostomy tube management and nutritional support." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'G-Tube Care',
      path: '/services/g-tube'
    }]} data-id="element-798" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-gradient-to-tr from-light-alt to-light" data-id="element-799">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-800">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-801">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-802">
              <UtensilsIcon className="h-4 w-4 text-primary" data-id="element-803" />
              <span className="text-primary text-sm font-semibold" data-id="element-804">
                Nutritional Support
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-805">
              Safe and Effective Tube Feeding Management
            </h2>
            <div className="space-y-5 text-dark-muted text-lg leading-relaxed" data-id="element-806">
              <p data-id="element-807">
                For patients who cannot take nutrition orally, we provide expert
                Gastrostomy tube management. This involves site care,
                administering physician-ordered formulas, and ensuring the tube
                remains secure and patent.
              </p>
            </div>

            {/* RN Badge */}
            <div className="mt-8 flex items-center bg-primary/5 border border-primary/10 rounded-xl p-5" data-id="element-808">
              <ShieldCheckIcon className="h-10 w-10 text-primary mr-4 flex-shrink-0" data-id="element-809" />
              <div data-id="element-810">
                <h4 className="font-bold text-dark" data-id="element-811">RN Supervised Care</h4>
                <p className="text-sm text-dark-muted" data-id="element-812">
                  All enteral nutrition plans are overseen by Registered Nurses
                  to ensure proper administration and safety.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-gradient-to-tr from-light to-light-alt" data-id="element-813">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-814">
          <div ref={benefitsRef} className={`transition-all duration-1000 ease-out ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-815">
            <div className="text-center mb-14" data-id="element-816">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-817">
                Comprehensive G-Tube Support
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-818"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-819">
              {[{
              icon: ShieldCheckIcon,
              title: 'Expert Tube Management',
              desc: 'Ensuring the tube remains secure, patent, and functioning correctly.'
            }, {
              icon: UtensilsIcon,
              title: 'Nutritional Support',
              desc: 'Careful administration of feeds to maintain optimal nutritional status.'
            }, {
              icon: ActivityIcon,
              title: 'Site Care',
              desc: 'Meticulous cleaning and monitoring of the stoma site to prevent infection.'
            }, {
              icon: ClipboardListIcon,
              title: 'Physician-Ordered Formulas',
              desc: 'Strict adherence to prescribed feeding schedules and formula types.'
            }].map((benefit, idx) => <div key={idx} className="bg-gradient-to-br from-light-alt to-light rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-200 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-820">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-821">
                    <benefit.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-822" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-3" data-id="element-823">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-824">
                    {benefit.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-20 bg-gradient-to-tr from-light-alt to-light" data-id="element-825">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-826">
          <div ref={detailsRef} className={`transition-all duration-1000 ease-out ${isDetailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-827">
            <h2 className="text-3xl font-bold text-dark mb-6" data-id="element-828">
              Our Enteral Nutrition Services Include
            </h2>
            <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-829">
              We provide complete management of enteral feeding systems to
              ensure patients receive the nutrition and hydration they need
              safely and comfortably.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-830">
              {['G-tube site cleaning & care', 'Formula administration per physician orders', 'Tube patency monitoring', 'Skin integrity assessment', 'Medication administration via tube', 'Nutritional status monitoring'].map((item, idx) => <div key={idx} className="flex items-center p-4 bg-light rounded-xl border border-gray-200" data-id="element-831">
                  <CheckCircleIcon className="h-6 w-6 text-secondary mr-3 flex-shrink-0" data-id="element-832" />
                  <span className="text-dark font-medium" data-id="element-833">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-light" data-id="element-834">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-835">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-836">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-837"></div>
            <div className="relative z-10" data-id="element-838">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-839">
                Need Expert G-Tube Care?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-840">
                Contact us today to discuss your nutritional care needs and
                schedule a consultation with our nursing team.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-841">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-842" />
    </main>;
}